import type {
  JobActionResponse,
  JobLogCountResponse,
  JobLogDataResponse,
  ServerListItem,
} from './types';

/**
 * Pull the message out of a failed response.
 *
 * The server sends `{"error": "..."}` on failure - see
 * `build_json_error_response` - but a 500 from a layer above the handler can
 * still arrive as HTML, so fall back to the status line.
 */
async function errorMessage(response: Response): Promise<string> {
  let message = `${response.status} ${response.statusText}`;

  try {
    const body = await response.json();
    if (body?.error) message = body.error;
  } catch {
    // Response body wasn't JSON - use status text
  }

  return message;
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as T;
}

/**
 * How many logs a job has. Zero is a normal answer, not an error - a job that
 * hasn't run yet has no log directory at all.
 */
export async function fetchJobLogCount(
  serverId: number,
  queueName: string,
  jobId: number,
): Promise<number> {
  const query = new URLSearchParams({ queue: queueName, job_id: String(jobId) });

  const body = await getJson<JobLogCountResponse>(
    `/admin/api/servers/${serverId}/jobs/log-count?${query}`,
  );

  // Validate at the boundary. A stale server build answers this route with a
  // different shape entirely, and an undefined count would otherwise flow on
  // into a request for log index "undefined".
  if (!Number.isInteger(body?.log_count) || body.log_count < 0) {
    throw new Error(`Unexpected log count response: ${JSON.stringify(body)}`);
  }

  return body.log_count;
}

/** The text of one of a job's logs. `index` is 1-based. */
export async function fetchJobLogData(
  serverId: number,
  queueName: string,
  jobId: number,
  index: number,
): Promise<string> {
  const query = new URLSearchParams({
    queue: queueName,
    job_id: String(jobId),
    index: String(index),
  });

  const body = await getJson<JobLogDataResponse>(
    `/admin/api/servers/${serverId}/jobs/log-data?${query}`,
  );

  if (typeof body?.data !== 'string') {
    throw new Error(`Unexpected log data response: ${JSON.stringify(body)}`);
  }

  return body.data;
}

/**
 * POST form-encoded parameters and parse the JSON that comes back.
 *
 * Form-encoded rather than JSON because that is the only body the server's
 * `form_data_from_request` accepts; it refuses anything else outright.
 */
async function postForm<T>(url: string, params: Record<string, string>): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(params).toString(),
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as T;
}

/** Every server this installation knows about, for the Move dialog. */
export async function fetchServers(): Promise<ServerListItem[]> {
  const body = await getJson<ServerListItem[]>('/admin/api/servers');

  if (!Array.isArray(body)) {
    throw new Error(`Unexpected server list response: ${JSON.stringify(body)}`);
  }

  return body;
}

/** Runs a job again on the same server. */
export function retryJob(
  serverId: number,
  queueName: string,
  jobId: number,
): Promise<JobActionResponse> {
  return postForm(`/admin/api/servers/${serverId}/jobs/retry`, {
    queue: queueName,
    job_id: String(jobId),
  });
}

/**
 * Stops a job, or clears away one that has already stopped.
 *
 * Which of those it is comes from the job's own row on the server, not from
 * here - the button's label is the only thing that differs at this end.
 */
export function cancelJob(
  serverId: number,
  queueName: string,
  jobId: number,
): Promise<JobActionResponse> {
  return postForm(`/admin/api/servers/${serverId}/jobs/cancel`, {
    queue: queueName,
    job_id: String(jobId),
  });
}

export function requeue(
    serverId: number,
): Promise<JobActionResponse> {
  return postForm(`/admin/api/servers/${serverId}/queues/requeue`, {});
}

/** Hands a job to a different server. */
export function moveJob(
  serverId: number,
  queueName: string,
  jobId: number,
  targetServerId: number,
): Promise<JobActionResponse> {
  return postForm(`/admin/api/servers/${serverId}/jobs/move`, {
    queue: queueName,
    job_id: String(jobId),
    target_server_id: String(targetServerId),
  });
}
